/**
 * safeStorage
 * --------------------------------------------------------------------------
 * iOS Safari Private Browsing throws QuotaExceededError on any
 * localStorage.setItem() call, and some embedded webviews disable
 * localStorage entirely. This wrapper degrades gracefully to an in-memory
 * Map so the app (especially the auth flow) never crashes on those devices.
 *
 * Drop-in replacement for the localStorage API surface we actually use:
 *   getItem / setItem / removeItem / clear
 */

const memoryStore = new Map();

let backingStore = null;
let probed = false;

function probeLocalStorage() {
  if (probed) return backingStore;
  probed = true;
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      backingStore = null;
      return null;
    }
    const testKey = '__axiom_storage_probe__';
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    backingStore = window.localStorage;
  } catch (e) {
    // Safari Private Browsing, disabled storage, quota exceeded, etc.
    backingStore = null;
  }
  return backingStore;
}

export const safeStorage = {
  getItem(key) {
    const ls = probeLocalStorage();
    if (ls) {
      try {
        return ls.getItem(key);
      } catch (e) {
        // fall through to memory
      }
    }
    return memoryStore.has(key) ? memoryStore.get(key) : null;
  },

  setItem(key, value) {
    const stringValue = String(value);
    // Always mirror to memory so reads work even when localStorage write fails
    memoryStore.set(key, stringValue);
    const ls = probeLocalStorage();
    if (ls) {
      try {
        ls.setItem(key, stringValue);
      } catch (e) {
        // Quota exceeded or private mode — memory fallback already captured it
      }
    }
  },

  removeItem(key) {
    memoryStore.delete(key);
    const ls = probeLocalStorage();
    if (ls) {
      try {
        ls.removeItem(key);
      } catch (e) {
        // ignore
      }
    }
  },

  clear() {
    memoryStore.clear();
    const ls = probeLocalStorage();
    if (ls) {
      try {
        ls.clear();
      } catch (e) {
        // ignore
      }
    }
  },

  /** True when the browser actually persists values across reloads. */
  isPersistent() {
    return probeLocalStorage() !== null;
  },
};

/**
 * Minimal user slice that we persist to localStorage.
 *
 * Tudor 11 Oct 2026: previously the full user record (email, name, sobriety
 * date and time, gender, country, payment status, avatar, Recovery Room
 * handle, etc.) was mirrored to localStorage under the "user" key. That is
 * personal + Recovery data and must not sit on disk. Only `current_week`
 * is read from storage (by GameTimerContext for session-length rules), so
 * that is all we store. Everything else is held in React state and
 * re-fetched from the server on reload.
 */
export function userTimerSlice(user) {
  if (!user || typeof user !== 'object') return {};
  const cw = user.current_week;
  return typeof cw === 'number' ? { current_week: cw } : {};
}

/**
 * Delete any legacy localStorage keys that should no longer be on disk.
 *
 * - `ph_*`: PostHog persistence rows left over from pre-9-Oct builds.
 *   PostHog now initialises with `persistence: "memory"` so nothing new is
 *   written, but existing users still have the old rows. One pass on every
 *   app load clears them so returning users are not stuck with stale IDs
 *   and feature-flag payloads sitting in storage.
 */
export function cleanupLegacyStorage() {
  const ls = probeLocalStorage();
  if (!ls) return { removed: [] };
  const removed = [];
  try {
    for (let i = ls.length - 1; i >= 0; i -= 1) {
      const k = ls.key(i);
      if (!k) continue;
      if (k.startsWith('ph_') || k.startsWith('ph__posthog')) {
        try { ls.removeItem(k); removed.push(k); } catch (_) { /* ignore */ }
      }
    }
  } catch (_) { /* ignore */ }
  return { removed };
}

export default safeStorage;